/* eslint-disable react/no-unstable-nested-components */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	type ColumnDef,
	type ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';

import ConfirmModal from '@/components/ConfirmModal/ConfirmModal';
import { DataTableColumnHeader } from '@/components/DataTable/DataTableColumnHeader';
import { DataTablePagination } from '@/components/DataTable/DataTablePagination';
import TableFilters from '@/components/TableFilters/TableFilters';
import { showErrorToast, showSuccessToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { deleteDiscipline, updateCourseDiscipline } from '@/hooks/mutations/disciplineMutations';
import { useGetDisciplines } from '@/hooks/queries/useDisciplines';
import { EditIcon, TrashCanIcon } from '@/icons';
import type { DisciplineWithCourses } from '@/lib/types';
import { themeSelectStyles } from '@/utils';

import { ConfirmationWarning } from './ConfirmationWarning';
import DisciplinesModal from './DisciplinesModal';

const Disciplines = () => {
	const [modalData, setModalData] = useState({
		isActive: false,
		isEditing: false,
		discipline: {} as DisciplineWithCourses,
	});
	const { data: disciplines, isFetching, isLoading } = useGetDisciplines();

	type DisciplineTableData = {
		name: string;
		numberOfCourses: number;
		color: string;
		actions: DisciplineWithCourses;
		idColumn: string;
	};

	const [filteredData, setFilteredData] = useState<DisciplineTableData[]>([]);
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
	const [isConfirming, setIsConfirming] = useState(false);
	const [isValidated, setIsValidated] = useState(true);
	const [newDiscipline, setNewDiscipline] = useState({
		id: '',
		name: '',
	});
	const queryClient = useQueryClient();

	const handleSetData = useCallback((queryDisciplineData: any) => {
		setFilteredData(queryDisciplineData as DisciplineTableData[]);
	}, []);

	const { mutateAsync: deleteDisciplineMutation } = useMutation({
		mutationFn: deleteDiscipline,
		onSuccess: (_, deletedValue) => {
			showSuccessToast('Discipline Deleted');

			// update query cache
			const schoolId = import.meta.env.VITE_SCHOOL_ID;
			const previousDisciplineData = queryClient.getQueryData(['get-all-disciplines', schoolId]) as
				| DisciplineWithCourses[]
				| undefined;
			if (previousDisciplineData) {
				const updatedDisciplines = previousDisciplineData.filter(
					(discipline) => discipline.id !== deletedValue.id
				);

				queryClient.setQueryData(['get-all-disciplines', schoolId], updatedDisciplines);
			}

			setTimeout(() => {
				queryClient.invalidateQueries({
					queryKey: ['get-admin-panel-courses'],
				});
			}, 100);
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const { mutateAsync: courseDisciplineMutation } = useMutation({
		mutationFn: updateCourseDiscipline,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['get-all-disciplines'],
			});
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const isAssigned = useCallback(
		(discipline: DisciplineWithCourses) =>
			Boolean(discipline.courses_disciplines && discipline?.courses_disciplines?.length >= 1),
		[]
	);

	const removeDiscipline = async (discipline: DisciplineWithCourses) => {
		if (!isAssigned(discipline)) {
			await deleteDisciplineMutation({
				id: discipline.id,
			});
			return;
		}

		await courseDisciplineMutation({
			newDiscipline: newDiscipline as any,
			previousDiscipline: discipline as any,
		});
	};

	const getOptionList = () => {
		const filteredDisciplines =
			disciplines?.filter((discipline) => discipline.id !== modalData.discipline.id) ?? [];

		const options = filteredDisciplines.map((discipline) => ({
			value: discipline.name,
			label: discipline.name,
			id: discipline.id,
		}));

		return options;
	};

	const confirmDelete = useCallback(
		(discipline: DisciplineWithCourses) => {
			setIsValidated(!isAssigned(discipline));
			setModalData({ isActive: false, isEditing: true, discipline });
			setIsConfirming(true);
		},
		[isAssigned]
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const abortController = new AbortController();
		return () => {
			abortController.abort();
		};
	}, [disciplines]);

	const queryData = useMemo(
		() =>
			disciplines?.map((discipline) => ({
				name: discipline.name,
				numberOfCourses: discipline.courses_disciplines?.length ?? 0,
				color: discipline.color,
				actions: discipline,
				idColumn: discipline.id,
			})),
		[disciplines]
	);

	useEffect(() => {
		if (queryData) {
			setFilteredData(queryData as DisciplineTableData[]);
		}
	}, [queryData]);

	const columns = useMemo<ColumnDef<DisciplineTableData>[]>(
		() => [
			{
				accessorKey: 'name',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
				cell: ({ row }) => <div>{row.getValue('name')}</div>,
			},
			{
				accessorKey: 'color',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Color" className="text-center" />
				),
				cell: ({ row }) => (
					<div className="flex justify-center">
						<div
							className="h-8 w-8 rounded-full"
							style={{ backgroundColor: row.getValue('color') }}
						/>
					</div>
				),
				enableSorting: false,
			},
			{
				accessorKey: 'numberOfCourses',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Assigned Courses" className="w-full" />
				),
				cell: ({ row }) => (
					<div className="flex justify-center">{row.getValue('numberOfCourses')}</div>
				),
			},
			{
				id: 'actions',
				enableHiding: false,
				cell: ({ row }) => {
					const updateDisciplineModal = () => {
						setModalData({
							isActive: true,
							isEditing: true,
							discipline: row.original.actions,
						});
					};

					return (
						<div className="flex items-center justify-center [&>button:not(:last-child)]:mr-4">
							<EditIcon passedEvent={updateDisciplineModal} />
							<TrashCanIcon
								passedEvent={() => {
									confirmDelete(row.original.actions);
								}}
							/>
						</div>
					);
				},
			},
		],
		[confirmDelete]
	);

	const table = useReactTable({
		data: filteredData,
		columns,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
		},
		initialState: {
			pagination: {
				pageSize: 10,
			},
		},
	});

	return (
		<>
			{isConfirming && (
				<ConfirmModal
					modalActive={isConfirming}
					onConfirm={() => removeDiscipline(modalData.discipline)}
					onCancel={() => setIsConfirming(false)}
					triggerModal={() => setIsConfirming(false)}
					message="Are you sure you want to delete the following discipline?"
					selectedData={modalData.discipline?.name ?? ''}
					validated={isValidated}
				>
					{isAssigned(modalData.discipline) && (
						<>
							<ConfirmationWarning discipline={modalData.discipline} />
							<Select
								data-testid="filterSelect"
								styles={themeSelectStyles}
								placeholder="Select a new discipline"
								onChange={(option) => {
									setNewDiscipline({
										id: `${option?.id}`,
										name: `${option?.label}`,
									});
									setIsValidated(true);
								}}
								isClearable
								options={[...getOptionList()]}
								noOptionsMessage={() => 'No Disciplines Found'}
							/>
						</>
					)}
				</ConfirmModal>
			)}
			{modalData.isActive && (
				<DisciplinesModal
					modalData={modalData}
					allDisciplines={disciplines}
					triggerModal={() =>
						setModalData({
							isActive: false,
							isEditing: false,
							discipline: {} as DisciplineWithCourses,
						})
					}
				/>
			)}
			<div className="flex items-center justify-between">
				<Button
					size="small"
					className="self-start mr-auto"
					onClick={() => {
						setModalData({
							isActive: true,
							isEditing: false,
							discipline: {} as DisciplineWithCourses,
						});
					}}
				>
					Add New Discipline
				</Button>
				{queryData && (
					<TableFilters
						data={queryData}
						handleSetData={handleSetData}
						searchColumn={['nameColumn']}
						filteredColumn="nameColumn"
						resultQty={filteredData.length}
						dropdown={false}
					/>
				)}
			</div>
			{(isFetching || isLoading) && (
				<div className="flex flex-col pb-5 h-full">
					<Skeleton className="w-full mb-3 h-[96px]" />
					<Skeleton className="w-full mb-3 h-[96px]" />
					<Skeleton className="w-full mb-3 h-[96px]" />
					<Skeleton className="w-full mb-3 h-[96px]" />
					<Skeleton className="w-full mb-3 h-[96px]" />
					<Skeleton className="w-full mb-3 h-[96px]" />
				</div>
			)}
			{!isFetching && (
				<div className="flex-1 flex flex-col min-h-0">
					<div
						className="flex-1 overflow-auto rounded-md"
						style={{ maxHeight: 'calc(100vh - 360px)' }}
					>
						<Table className="text-md typo-body-2">
							<TableHeader className="sticky top-0 bg-background z-10">
								{table.getHeaderGroups().map((headerGroup) => (
									<TableRow key={headerGroup.id}>
										{headerGroup.headers.map((header) => {
											return (
												<TableHead key={header.id}>
													{header.isPlaceholder
														? null
														: flexRender(header.column.columnDef.header, header.getContext())}
												</TableHead>
											);
										})}
									</TableRow>
								))}
							</TableHeader>
							<TableBody>
								{table.getRowModel().rows?.length ? (
									table.getRowModel().rows.map((row) => (
										<TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
											{row.getVisibleCells().map((cell) => (
												<TableCell key={cell.id}>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</TableCell>
											))}
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={columns.length} className="h-24 text-center">
											No results.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
					<div className="mt-4 shrink-0 pb-4 border-t border-gray-200 pt-4">
						<DataTablePagination table={table} />
					</div>
				</div>
			)}
		</>
	);
};

export default Disciplines;
